import { DomainRuleViolationError } from "./errors.js";
import { boundedText } from "./bounded-text.js";
import { DomainValidationError } from "./identifiers.js";
import { money } from "./money.js";
import { ianaTimeZone } from "./time-zone.js";
// Frozen: the database CHECK constraint is generated from this tuple.
export const conciergeRequestTypes = Object.freeze([
    "DINING",
    "VILLA",
    "EVENT",
    "EXPERIENCE",
    "SERVICE",
    "TRANSPORT",
    "OTHER",
]);
/** `requirements_version` stored with `requirements_details`; bump it when a type's keys change. */
export const REQUIREMENTS_VERSION = 1;
/** The database CHECK bound on `octet_length(requirements_details::text)`. */
export const REQUIREMENTS_DETAILS_MAX_BYTES = 4096;
const text = { type: "string" };
const strings = { type: "strings" };
// Descriptive preferences only: nothing a rule, a transition, expiry, routing, payment or a join
// depends on lives here (those are typed columns), and no SQL predicate reads this object.
const DETAIL_FIELDS = {
    DINING: {
        occasion: text,
        cuisinePreferences: strings,
        dietaryRequirements: strings,
        seatingPreference: text,
        mealPeriod: { type: "enum", values: ["BREAKFAST", "LUNCH", "DINNER", "OTHER"] },
    },
    VILLA: {
        bedroomsMin: { type: "integer", min: 1, max: 30 },
        amenities: strings,
        privacyPreference: text,
    },
    EVENT: { eventName: text, seatingPreference: text },
    EXPERIENCE: {
        experienceKind: text,
        privateOnly: { type: "boolean" },
        accessibilityNeeds: text,
    },
    SERVICE: { serviceKind: text },
    TRANSPORT: {
        pickupLocation: text,
        dropoffLocation: text,
        luggageCount: { type: "integer", min: 0, max: 50 },
        vehiclePreference: text,
    },
    OTHER: {},
};
export const requirementsDetailsKeys = Object.freeze(Object.fromEntries(conciergeRequestTypes.map((type) => [type, Object.freeze(Object.keys(DETAIL_FIELDS[type]))])));
/** What must be present before DRAFT -> SUBMITTED. `details.x` names a key of the details object. */
export const minimumIntentFields = Object.freeze({
    DINING: Object.freeze(["dateFrom", "preferredTime", "timeZone", "partySize", "locationText"]),
    VILLA: Object.freeze(["dateFrom", "dateTo", "timeZone", "partySize", "locationText"]),
    EVENT: Object.freeze(["dateFrom", "timeZone", "partySize"]),
    EXPERIENCE: Object.freeze(["dateFrom", "timeZone", "partySize", "locationText"]),
    SERVICE: Object.freeze(["specialRequests"]),
    TRANSPORT: Object.freeze([
        "dateFrom",
        "preferredTime",
        "timeZone",
        "details.pickupLocation",
        "details.dropoffLocation",
    ]),
    OTHER: Object.freeze(["specialRequests"]),
});
/**
 * Every field name `minimumIntentFields` can list: the closed codes of a `NEEDS_DETAILS` answer (CHT-749). A test
 * fails when a type lists a name that is not here.
 */
export const minimumIntentFieldNames = Object.freeze([
    "dateFrom",
    "dateTo",
    "preferredTime",
    "timeZone",
    "partySize",
    "locationText",
    "specialRequests",
    "details.pickupLocation",
    "details.dropoffLocation",
]);
const IANA_ZONE = /^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+){1,2})$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
const fail = (field, rule) => new DomainValidationError(field, rule);
const optional = (value) => value !== null && value !== undefined;
function isoDate(field, value) {
    const match = typeof value === "string" ? DATE.exec(value) : null;
    if (match === null)
        throw fail(field, "must be a YYYY-MM-DD date");
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (probe.getUTCFullYear() !== year ||
        probe.getUTCMonth() !== month - 1 ||
        probe.getUTCDate() !== day) {
        throw fail(field, "must be a real calendar date");
    }
    return value;
}
function wallTime(field, value) {
    const match = typeof value === "string" ? TIME.exec(value) : null;
    if (match === null)
        throw fail(field, "must be an HH:MM time");
    return `${match[1]}:${match[2]}:${match[3] ?? "00"}`;
}
function validateDetails(type, details) {
    const fields = DETAIL_FIELDS[type];
    const out = {};
    if (details === null || details === undefined)
        return out;
    if (typeof details !== "object" || Array.isArray(details)) {
        throw fail("requirementsDetails", "must be an object");
    }
    for (const key of Object.keys(details)) {
        if (!Object.hasOwn(fields, key))
            throw fail("requirementsDetails", "contains an unsupported key");
    }
    for (const [key, kind] of Object.entries(fields)) {
        const value = details[key];
        if (value === undefined || value === null)
            continue;
        const field = `requirementsDetails.${key}`;
        switch (kind.type) {
            case "string":
                out[key] = boundedText(field, value, 200);
                break;
            case "strings": {
                if (!Array.isArray(value) || value.length > 10) {
                    throw fail(field, "must be a list of at most 10 strings");
                }
                out[key] = Object.freeze(value.map((item) => boundedText(field, item, 50)));
                break;
            }
            case "integer":
                if (!Number.isSafeInteger(value) ||
                    value < kind.min ||
                    value > kind.max) {
                    throw fail(field, `must be an integer from ${kind.min} to ${kind.max}`);
                }
                out[key] = value;
                break;
            case "boolean":
                if (typeof value !== "boolean")
                    throw fail(field, "must be a boolean");
                out[key] = value;
                break;
            case "enum":
                if (typeof value !== "string" || !kind.values.includes(value)) {
                    throw fail(field, "must be one of the allowed values");
                }
                out[key] = value;
                break;
        }
    }
    if (requirementsDetailsBytes(out) > REQUIREMENTS_DETAILS_MAX_BYTES) {
        throw fail("requirementsDetails", `must be at most ${REQUIREMENTS_DETAILS_MAX_BYTES} bytes`);
    }
    return out;
}
/**
 * The size PostgreSQL measures for the `requirements_details` CHECK, `octet_length(jsonb::text)`.
 * `jsonb::text` prints one space after each colon and comma, which `JSON.stringify` does not.
 */
export function requirementsDetailsBytes(value) {
    const render = (node) => {
        if (Array.isArray(node))
            return `[${node.map(render).join(", ")}]`;
        if (node !== null && typeof node === "object") {
            return `{${Object.entries(node)
                .map(([key, item]) => `${JSON.stringify(key)}: ${render(item)}`)
                .join(", ")}}`;
        }
        return JSON.stringify(node);
    };
    return new TextEncoder().encode(render(value)).length;
}
/**
 * Validates the typed core and the type's details on every write; the result is what is stored.
 * Messages name the field and rule, never the rejected value.
 */
export function validateRequestRequirements(type, input) {
    if (!conciergeRequestTypes.includes(type))
        throw fail("requestType", "must be a known type");
    const title = boundedText("title", input.title, 140);
    let partySize = null;
    if (optional(input.partySize)) {
        if (!Number.isSafeInteger(input.partySize) || input.partySize < 1 || input.partySize > 100) {
            throw fail("partySize", "must be an integer from 1 to 100");
        }
        partySize = input.partySize;
    }
    const dateFrom = optional(input.dateFrom) ? isoDate("dateFrom", input.dateFrom) : null;
    const dateTo = optional(input.dateTo) ? isoDate("dateTo", input.dateTo) : null;
    if (dateTo !== null && (dateFrom === null || dateTo < dateFrom)) {
        throw fail("dateTo", "must not be before dateFrom");
    }
    const preferredTime = optional(input.preferredTime)
        ? wallTime("preferredTime", input.preferredTime)
        : null;
    // The shape first (the database CHECK), then the runtime must know the zone (CHT-749): a made-up name is refused here, not stored.
    let timeZone = null;
    if (optional(input.timeZone)) {
        if (typeof input.timeZone !== "string" || !IANA_ZONE.test(input.timeZone)) {
            throw fail("timeZone", "must be an IANA time zone name");
        }
        timeZone = ianaTimeZone(input.timeZone);
    }
    if ((dateFrom !== null || preferredTime !== null) && timeZone === null) {
        throw fail("timeZone", "is required with a date or a time");
    }
    let indicativeBudget = null;
    if (optional(input.indicativeBudget)) {
        try {
            indicativeBudget = money(input.indicativeBudget.amountMinor, input.indicativeBudget.currency);
        }
        catch {
            throw fail("indicativeBudget", "must be a non-negative integer amount and a currency code");
        }
    }
    let neededBy = null;
    if (optional(input.neededBy)) {
        if (!(input.neededBy instanceof Date) || Number.isNaN(input.neededBy.getTime())) {
            throw fail("neededBy", "must be a valid date");
        }
        neededBy = input.neededBy;
    }
    return Object.freeze({
        title,
        partySize,
        dateFrom,
        dateTo,
        preferredTime,
        timeZone,
        locationText: optional(input.locationText)
            ? boundedText("locationText", input.locationText, 200)
            : null,
        specialRequests: optional(input.specialRequests)
            ? boundedText("specialRequests", input.specialRequests, 2000, { multiline: true })
            : null,
        indicativeBudget,
        neededBy,
        requirementsVersion: REQUIREMENTS_VERSION,
        details: Object.freeze(validateDetails(type, input.details)),
    });
}
/** The minimum-intent fields of `type` that the source does not hold yet, in the order of `minimumIntentFields`. */
export function missingMinimumIntentFields(type, source) {
    return minimumIntentFields[type].filter((field) => {
        const value = field.startsWith("details.")
            ? source.details?.[field.slice("details.".length)]
            : source[field];
        return value === null || value === undefined;
    });
}
/** DRAFT -> SUBMITTED needs the type's minimum structured intent: `request.requirements_incomplete`. */
export function assertMinimumIntent(type, source) {
    if (missingMinimumIntentFields(type, source).length > 0) {
        throw new DomainRuleViolationError("request.requirements_incomplete");
    }
}
